import { useCallback, useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { getWelcomeSheet, sendWelcomeText } from '@/services/intake.service.js';
import { toastSmsWarnings } from '@/lib/smsWarnings.js';
import SmsPermissionControl from '@/components/intake/SmsPermissionControl.jsx';

// MV-01 3.5 (Ship 1b): the broker UI for the 1a welcome-text endpoints.
// One-tap send with an editable prefilled body; the text permission store's
// answer is always visible (word beside the colour, with David's Record
// permission / Stop texting handle, T7e); a number that texted STOP
// is a hard block. Whether a send without permission goes is the permission
// gate's decision (people-create-door T7c): refused 409/503 when it enforces,
// sent with a warning toast in shadow. The old no-consent confirm checkbox is
// gone: it overrode a fact the gate now owns. Broker-triggered ONLY, never
// automatic. Every send lands on the deal timeline (communications).
export default function WelcomeTextSheet({ dealId, open, onOpenChange, onChange }) {
  const [sheet, setSheet] = useState(null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState('');

  // A re-read after a permission change keeps the edited body.
  const load = useCallback(() => getWelcomeSheet(dealId)
    .then((d) => { setSheet(d.sheet); return d.sheet; })
    .catch((err) => setLoadError(err instanceof ApiError ? (err.body?.error || err.code) : err.message)), [dealId]);

  useEffect(() => {
    if (!open || !dealId) return;
    setSheet(null);
    setLoadError('');
    load().then((s) => setBody(s?.body || ''));
  }, [open, dealId, load]);

  const optedOut = sheet?.opted_out === true;
  const canSend = !!sheet && !optedOut && !!sheet.phone && !sending;

  async function handleSend() {
    setSending(true);
    try {
      const res = await sendWelcomeText(dealId, { message: body });
      toast.success(`Welcome text sent to ${sheet.contact_name || sheet.phone}`);
      toastSmsWarnings(res?.warnings);
      onChange?.();
      onOpenChange?.(false);
    } catch (err) {
      const code = err instanceof ApiError ? err.body?.code : null;
      if (code === 'SMS_OPTED_OUT') {
        toast.error('This number has opted out of texts. Send blocked.');
      } else if (code === 'SMS_NO_PERMISSION' || code === 'SMS_PERMISSION_UNREADABLE') {
        toast.error(err.body?.error || 'Text not sent: no text permission.');
      } else {
        toast.error(`Send failed: ${err.message || 'unknown error'}`);
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Send welcome text</SheetTitle>
          <SheetDescription>
            One tap, never automatic. Prefilled from the admin template; edit before sending.
          </SheetDescription>
        </SheetHeader>

        {loadError ? (
          <p className="mt-6 text-sm text-danger">{loadError}</p>
        ) : !sheet ? (
          <p className="mt-6 text-sm text-text-muted">Loading…</p>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="rounded border border-border bg-surface px-3 py-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-text">{sheet.contact_name || 'Client'}</span>
                <span className="text-text-muted">{sheet.phone || 'No phone on intake'}</span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <SmsPermissionControl phone={sheet.phone} state={sheet.permission} at={sheet.permission_at} crmContactId={sheet.crm_contact_id} onChanged={load} />
                {sheet.already_sent ? (
                  <span className="rounded-full bg-surface-strong px-2 py-0.5 text-[11px] text-text-muted">
                    Already sent {String(sheet.already_sent.sent_at || '').slice(0, 10)}{sheet.already_sent.by ? ` by ${sheet.already_sent.by}` : ''}
                  </span>
                ) : null}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-text-muted">Message</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
                disabled={optedOut}
              />
            </div>

            <Button onClick={handleSend} disabled={!canSend} className="w-full">
              {sending ? 'Sending…' : optedOut ? 'Blocked (opted out)' : 'Send welcome text'}
            </Button>
            <p className="text-[11px] text-text-dim">Logged on the deal timeline. No portal link in this text; that arrives with quote presentation.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
